import { ExternalLink } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import type { List } from '@shared/types';

interface LinkedInSourceLinkProps {
  list: List;
  variant?: 'icon' | 'button' | 'link' | 'badge';
  className?: string;
  showLabel?: boolean;
}

/**
 * LinkedIn Source Link Component
 * Zeigt Link zur originalen LinkedIn-Quelle (aus sourceUrl)
 */
export function LinkedInSourceLink({ 
  list, 
  variant = 'button',
  className = '',
  showLabel = true
}: LinkedInSourceLinkProps) {
  
  // Kein Link wenn sourceUrl fehlt
  if (!list.sourceUrl) {
    return null;
  }
  
  // Source-Labels
  const sourceLabel = getSourceLabel(list.source);
  const shortLabel = getShortLabel(list.source);
  
  // Basis-Link-Props
  const linkProps = {
    href: list.sourceUrl,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: (e: React.MouseEvent) => e.stopPropagation()
  };
  
  // Icon Variant (minimalistisch für Tabellen)
  if (variant === 'icon') {
    return (
      <a
        {...linkProps}
        title={`Open ${sourceLabel} in LinkedIn`}
        className={`inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors ${className}`}
      >
        <ExternalLink className="h-4 w-4" />
      </a>
    );
  }
  
  // Link Variant (Text + Icon)
  if (variant === 'link') {
    return (
      <a
        {...linkProps}
        className={`inline-flex items-center gap-1 text-sm text-blue-600 hover:underline transition-colors ${className}`}
      >
        {showLabel && <span>{shortLabel}</span>}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }
  
  // Badge Variant (kompakt)
  if (variant === 'badge') {
    return (
      <a
        {...linkProps}
        className={className}
      >
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-blue-50 transition-colors gap-1"
        >
          {showLabel && <span>{shortLabel}</span>}
          <ExternalLink className="h-3 w-3" />
        </Badge>
      </a>
    );
  }
  
  // Button Variant (Standard, prominent)
  return (
    <a
      {...linkProps}
      className={className}
    >
      <Button
        variant="outline"
        size="sm"
        type="button"
      >
        <ExternalLink className="h-4 w-4 mr-2" />
        {showLabel ? `View ${shortLabel}` : 'View Source'}
      </Button>
    </a>
  );
}

/**
 * Vollständige Source-Labels
 */
function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    'linkedin-search': 'LinkedIn People Search',
    'linkedin-group': 'LinkedIn Group Members',
    'linkedin-company': 'LinkedIn Company Employees',
    'linkedin-event': 'LinkedIn Event Attendees',
    'linkedin-single': 'LinkedIn Profile',
    'manual': 'Manual List',
    'import': 'Imported List',
    'scraper': 'LinkedIn Source'
  };
  
  return labels[source] || 'LinkedIn Source';
}

/**
 * Kurze Source-Labels (für kompakte UI)
 */
function getShortLabel(source: string): string {
  const labels: Record<string, string> = {
    'linkedin-search': 'Search',
    'linkedin-group': 'Group',
    'linkedin-company': 'Company',
    'linkedin-event': 'Event',
    'linkedin-single': 'Profile',
    'manual': 'Manual',
    'import': 'Import',
    'scraper': 'Source'
  };
  
  return labels[source] || 'Source';
}